import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'

import { sendYandexMetricaEvent } from '@/shared/integrations/yandex-metric/utils/send-metric-event'
import { RECAPTCHA_SITE_KEY } from '@/shared/lib/utils/constants/const'
import { WEB_CONSEST_LINK, WEB_POLICY_LINK } from '@/shared/lib/utils/constants/pdfDocs'
import { formatPhoneMasked, PHONE_MASKED_REGEX } from '@/shared/lib/validation/phone'
import { Button, RHFPhoneInput } from '@/shared/ui'

import { AuthByAlfaBank } from '../../../by-alfabank'
import { formatMinutesSeconds } from '../../lib/format-minutes-seconds'
import { AuthByPhoneForm, AuthByPhoneSchema } from '../../types/auth-schema'
import { useAuthByPhoneContext } from '../auth-by-phone/auth-by-phone.context'

import cl from './Phone.module.scss'

export const Phone = () => {
	const { phone: committedPhone, isLoading, isCooldown, resendCooldown, startLogin } = useAuthByPhoneContext()
	const [isCaptchaVisible, setIsCaptchaVisible] = useState(false)
	const [captchaToken, setCaptchaToken] = useState('')
	const recaptchaRef = useRef<ReCAPTCHA>(null)
	const { control, handleSubmit, watch, setError } = useForm<AuthByPhoneForm>({
		mode: 'onBlur',
		resolver: zodResolver(AuthByPhoneSchema),
		defaultValues: committedPhone ? { phone: formatPhoneMasked(committedPhone) } : undefined
	})

	const submitLogin = async (phoneNumber: string, token?: string) => {
		const result = await startLogin(phoneNumber, token)
		if (result?.status === 'captcha_required') {
			setIsCaptchaVisible(true)
			return
		}
		if (result?.status === 'error') {
			setError('phone', { message: result.message })
			setCaptchaToken('')
			recaptchaRef.current?.reset()
		}
	}

	const onSubmit = handleSubmit(async ({ phone: phoneNumber }) => {
		if (isLoading || isCooldown) return

		try {
			await submitLogin(phoneNumber, captchaToken || undefined)
		} finally {
			sendYandexMetricaEvent(`recieve_code`)
		}
	})

	const onCaptchaChange = (value: string | null) => {
		if (!value) return
		setCaptchaToken(value)
		handleSubmit(({ phone: phoneNumber }) => submitLogin(phoneNumber, value))()
	}

	const phone = watch('phone')

	const submitDisabled = useMemo(
		() => isLoading || isCooldown || !phone || !PHONE_MASKED_REGEX.test(phone) || isCaptchaVisible,
		[isLoading, isCooldown, phone, isCaptchaVisible]
	)

	return (
		<div className={cl.root}>
			<p className={cl.title}>Вход или регистрация</p>
			<p className={cl.subtitle}>Введите номер телефона</p>
			<form onSubmit={onSubmit}>
				<div className={cl.input}>
					<RHFPhoneInput name="phone" control={control} isLoading={isLoading} disabled={isCooldown} fullWidth />
					{isCaptchaVisible && (
						<div className={cl.captcha}>
							<ReCAPTCHA ref={recaptchaRef} onChange={onCaptchaChange} sitekey={RECAPTCHA_SITE_KEY} />
						</div>
					)}
				</div>
				<Button className={clsx('gtm-get-code', cl.button)} fullWidth type="submit" disabled={submitDisabled}>
					Получить код
				</Button>
			</form>
			<p className={cl.rights}>
				Нажимая на кнопку, вы соглашаетесь с
				<a href={WEB_POLICY_LINK} target="_blank">
					{' политикой конфиденциальности '}
				</a>
				и
				<a href={WEB_CONSEST_LINK} target="_blank">
					{' обработкой персональных данных '}
				</a>
			</p>
			{isCooldown && <p className={cl.countdown}>Авторизация доступна через {formatMinutesSeconds(resendCooldown)}</p>}
			<AuthByAlfaBank />
		</div>
	)
}
